import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, Search, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR } from "@/lib/api";
import { PageHeader, Pager, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", business_name: "", email: "", mobile: "", address: "", city: "", state: "", pincode: "", gstin: "", pan: "", password: "" };

export default function Agents() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [created, setCreated] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["agents", page, search],
    queryFn: async () => (await api.get(`/admin/agents?page=${page}&limit=20&search=${encodeURIComponent(search)}`)).data,
  });

  const create = useMutation({
    mutationFn: async () => (await api.post("/admin/agents", form)).data,
    onSuccess: (d) => { setCreated(d); setForm(EMPTY); qc.invalidateQueries({ queryKey: ["agents"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const toggle = useMutation({
    mutationFn: async ({ id, status }) => api.put(`/admin/agents/${id}`, { status }),
    onSuccess: () => { toast.success("Agent updated"); qc.invalidateQueries({ queryKey: ["agents"] }); },
  });
  const [delAgent, setDelAgent] = useState(null);
  const [purge, setPurge] = useState(false);
  const del = useMutation({
    mutationFn: async ({ id, purge }) => api.delete(`/admin/agents/${id}${purge ? "?purge=true" : ""}`),
    onSuccess: () => { toast.success("Agent deleted"); setDelAgent(null); setPurge(false); qc.invalidateQueries({ queryKey: ["agents"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div data-testid="agents-page">
      <PageHeader title="Agents" sub="Create agents, control access, assign products." testid="agents-title">
        <Button data-testid="add-agent-btn" onClick={() => { setCreated(null); setOpen(true); }} className="bg-orange-500 text-white hover:bg-orange-600">
          <Plus size={14} /> New Agent
        </Button>
      </PageHeader>

      <div className="relative mb-4 max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input data-testid="agent-search-input" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search agents…" className="border-slate-700 bg-[#111827] pl-9 text-slate-100 placeholder:text-slate-600" />
      </div>

      {isLoading ? <p className="text-sm text-slate-500">Loading…</p> : !data?.items?.length ? <EmptyState title="No agents yet" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-800 bg-[#111827]">
          <table className="w-full text-left text-xs" data-testid="agents-table">
            <thead>
              <tr className="border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Agent</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Products</th>
                <th className="px-4 py-3">Orders</th>
                <th className="px-4 py-3">Sales</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((a) => (
                <tr key={a.id} data-testid={`agent-row-${a.id}`} className="border-b border-slate-800/50 hover:bg-slate-900/40">
                  <td className="px-4 py-3">
                    <Link to={`/admin/agents/${a.id}`} data-testid={`agent-link-${a.id}`} className="font-medium text-slate-100 hover:text-orange-400">{a.business_name}</Link>
                    <p className="text-[10px] text-slate-500">{a.name}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-400">{a.email}<br /><span className="font-mono text-[10px]">{a.mobile}</span></td>
                  <td className="px-4 py-3 text-slate-300">{a.products}</td>
                  <td className="px-4 py-3 text-slate-300">{a.orders}</td>
                  <td className="px-4 py-3 text-slate-300">{fmtINR(a.sales)}</td>
                  <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Button data-testid={`toggle-agent-${a.id}`} size="sm" variant="outline"
                        onClick={() => toggle.mutate({ id: a.id, status: a.status === "active" ? "inactive" : "active" })}
                        className="border-slate-700 text-[10px] text-slate-400 hover:bg-slate-800">
                        {a.status === "active" ? "Deactivate" : "Activate"}
                      </Button>
                      <button data-testid={`delete-agent-${a.id}`} onClick={() => { setDelAgent(a); setPurge(false); }}
                        className="rounded p-1.5 text-slate-500 transition-colors hover:bg-rose-500/10 hover:text-rose-500" title="Delete agent">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <Pager page={data.page} pages={data.pages} onPage={setPage} />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg border-slate-800 bg-[#111827] text-slate-100">
          <DialogHeader><DialogTitle>New Agent</DialogTitle></DialogHeader>
          {created ? (
            <div data-testid="agent-created" className="space-y-3">
              <p className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-400">
                Agent created. Share these login credentials:
              </p>
              <div className="rounded-lg border border-slate-700 bg-slate-900 p-3 font-mono text-xs text-slate-300">
                <p>Email: {created.login.email}</p>
                <p>Password: {created.login.password}</p>
              </div>
              <Button data-testid="agent-created-close" onClick={() => setOpen(false)} className="w-full bg-orange-500 text-white hover:bg-orange-600">Done</Button>
            </div>
          ) : (
            <div className="max-h-[65vh] space-y-3 overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                {[["name", "Full Name *"], ["business_name", "Business Name *"], ["email", "Email *"], ["mobile", "Mobile *"],
                  ["city", "City"], ["state", "State"], ["pincode", "Pincode"], ["gstin", "GSTIN"], ["pan", "PAN"], ["password", "Password (default Agent@123)"]].map(([k, label]) => (
                  <div key={k} className={k === "password" ? "col-span-2" : ""}>
                    <Label className="text-xs text-slate-400">{label}</Label>
                    <Input data-testid={`agent-${k}-input`} value={form[k]} onChange={(e) => set(k, e.target.value)}
                      className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
                  </div>
                ))}
              </div>
              <div>
                <Label className="text-xs text-slate-400">Address</Label>
                <Input data-testid="agent-address-input" value={form.address} onChange={(e) => set("address", e.target.value)}
                  className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
              </div>
              <Button data-testid="create-agent-submit" disabled={create.isPending || !form.name || !form.business_name || !form.email || !form.mobile}
                onClick={() => create.mutate()} className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600">
                <KeyRound size={14} className="mr-1" /> {create.isPending ? "Creating…" : "Create agent"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!delAgent} onOpenChange={() => setDelAgent(null)}>
        <DialogContent className="border-slate-800 bg-[#111827] text-slate-100">
          <DialogHeader><DialogTitle>Delete {delAgent?.business_name}?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-xs">
            <p className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-rose-300" data-testid="delete-agent-warning">
              Permanently removes the agent, their login, basket, stores, marketplace selections, subscription and invoices. Cannot be undone.
            </p>
            {(delAgent?.orders ?? 0) > 0 && (
              <label className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-300" data-testid="purge-orders-wrap">
                <input type="checkbox" data-testid="purge-orders-check" checked={purge} onChange={(e) => setPurge(e.target.checked)} className="mt-0.5" />
                <span>This agent has {delAgent.orders} order(s). Tick to also permanently delete those orders, payments and shipments.</span>
              </label>
            )}
            <Button data-testid="delete-agent-confirm" disabled={del.isPending || ((delAgent?.orders ?? 0) > 0 && !purge)}
              onClick={() => del.mutate({ id: delAgent.id, purge })}
              className="w-full bg-rose-600 font-semibold text-white hover:bg-rose-500">
              {del.isPending ? "Deleting…" : "Delete agent permanently"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
